import { useCallback } from 'react';
import { useDataCache } from '../../contexts/BreakdownDataCacheContext';
import { updateScene, updateScenesBatch, deleteScene, createScene } from '../../api/breakdown';
import { SceneInfo, UpdateSceneParams } from '../../types/scene.types';
import type { CalDateDataCache, SlotDataCache } from '../../types/cache.types';
import type { CalDateInfo, SlotInfo } from '../../types/schedule.types';
import { SlotType } from '../../types/schedule.types';
import type { ElementSceneInfo } from '../../types/element.types';
import { normalizeToSortedIds } from '../../utils/calDateSlotsSortUtils';

function sortIdsHasNonNoteSolt(
  sortIds: string[],
  scheduleId: string,
  getSlotCache: (sid: string) => SlotDataCache | null,
): boolean {
  if (sortIds.length === 0) return false;
  const slots = getSlotCache(scheduleId)?.slots ?? [];
  const map = new Map(slots.map((t) => [t.slotID, t]));
  return sortIds.some((id) => {
    const t = map.get(id);
    return Boolean(t && (t.slotType === SlotType.SHOOT || !t.slotType));
  });
}

/**
 * Scene 更新 Hook 配置选项
 */
export interface UseSceneUpdatesOptions {
  onSuccess?: (sceneId: string, updates: UpdateSceneParams) => void;
  onError?: (error: Error, sceneId: string, updates: UpdateSceneParams) => void;
}

/**
 * Scene 更新 Hook
 * 提供统一的 Scene 数据更新方法，包括：
 * - 单条/批量更新
 * - 单条/批量删除
 * - 单条创建
 * 
 * 更新流程：
 * 1. 乐观更新缓存
 * 2. 通知本标签页订阅者（UI 立即更新）
 * 3. 调用 API
 * 4. 成功：通过 BroadcastChannel 通知其他标签页
 * 5. 失败：回滚缓存，通知订阅者
 */
export function useSceneUpdates(options?: UseSceneUpdatesOptions) {
  const {
    getBreakdownCache,
    setBreakdownCache,
    getElementRelationCache,
    setElementRelationCache,
    getScheduleCache,
    setScheduleCache,
    getSlotCache,
    setSlotCache,
    getCalDateCache,
    setCalDateCache,
  } = useDataCache();

  /**
   * 更新单个 Scene（纯 API 调用函数，不涉及缓存操作）
   * 缓存操作由 BreakdownDataCacheContext 统一处理
   */
  const updateSceneData = useCallback(
    async (sceneId: string, updates: UpdateSceneParams, breakdownId?: string) => {
      try {
        // 调用 API
        await updateScene(sceneId, updates);

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess(sceneId, updates);
        }
      } catch (error) {
        console.error('[useSceneUpdates] 更新 Scene 失败:', error);

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, sceneId, updates);
        }

        throw error;
      }
    },
    [options]
  );

  /**
   * 批量更新场次行（scenes，本地缓存 + batch API）
   */
  const updateScenesData = useCallback(
    async (
      updates: Array<{ sceneId: string; data: UpdateSceneParams }>,
      breakdownId?: string
    ) => {
      let oldCache: any = null;
      let oldScenes: any[] = [];

      try {
        // 如果提供了 breakdownId，进行乐观更新
        if (breakdownId) {
          const cache = getBreakdownCache(breakdownId);
          if (cache) {
            oldCache = { ...cache };
            oldScenes = [...cache.scenes];

            // 创建更新映射
            const updateMap = new Map(updates.map((u) => [u.sceneId, u.data]));

            // 乐观更新缓存中的 scenes（场次行）
            const updatedScenes = cache.scenes.map((scene) => {
              const update = updateMap.get(scene.sceneID);
              return update ? { ...scene, ...update } : scene;
            });

            setBreakdownCache(
              breakdownId,
              {
                scenes: updatedScenes,
              },
              { skipAutoSceneSync: true }
            );
          }
        }

        // 调用批量更新 API（单次请求，提升性能）
        const batchPayload = updates.map((u) => ({
          sceneID: u.sceneId,
          ...u.data,
        }));
        await updateScenesBatch(batchPayload);

        // 成功回调
        updates.forEach((update) => {
          if (options?.onSuccess) {
            options.onSuccess(update.sceneId, update.data);
          }
        });
      } catch (error) {
        console.error('[useSceneUpdates] 批量更新 scenes（场次行）失败:', error);

        // 回滚缓存
        if (breakdownId && oldCache) {
          setBreakdownCache(
            breakdownId,
            {
              scenes: oldScenes,
            },
            { skipAutoSceneSync: true }
          );
        }

        // 错误回调
        updates.forEach((update) => {
          if (options?.onError) {
            options.onError(error as Error, update.sceneId, update.data);
          }
        });

        throw error;
      }
    },
    [getBreakdownCache, setBreakdownCache, options]
  );

  /**
   * 删除单个 Scene
   */
  const deleteSceneData = useCallback(
    async (sceneId: string, breakdownId?: string) => {
      let oldCache: any = null;
      let oldScenes: any[] = [];
      const deletedSceneIds = new Set([sceneId]);
      let oldElementRelations: ElementSceneInfo[] | null = null;
      const oldSlotCaches = new Map<string, SlotDataCache>();
      const oldCalDateCaches = new Map<string, CalDateDataCache>();
      let oldScheduleCurrentSlice: {
        currentSlots?: SlotInfo[];
        currentCalDates?: CalDateInfo[];
      } | null = null;

      try {
        // 如果提供了 breakdownId，进行乐观更新
        if (breakdownId) {
          const cache = getBreakdownCache(breakdownId);
          if (cache) {
            oldCache = { ...cache };
            oldScenes = [...cache.scenes];

            // 乐观删除缓存中的 scene
            const updatedScenes = cache.scenes.filter((scene) => scene.sceneID !== sceneId);

            setBreakdownCache(breakdownId, {
              scenes: updatedScenes,
            });
          }

          const rel = getElementRelationCache(breakdownId);
          if (rel?.elementRelations?.length) {
            const nextRelations = rel.elementRelations.filter((r) => !deletedSceneIds.has(r.sceneID));
            if (nextRelations.length !== rel.elementRelations.length) {
              oldElementRelations = [...rel.elementRelations];
              setElementRelationCache(breakdownId, { elementRelations: nextRelations });
            }
          }

          const removedSoltIdsBySchedule = new Map<string, Set<string>>();
          const scheduleListSlice = getScheduleCache(breakdownId);
          for (const scheduleRow of scheduleListSlice?.schedules ?? []) {
            const scheduleID = scheduleRow.scheduleID;
            const slotCache = getSlotCache(scheduleID);
            if (!slotCache?.slots?.length) continue;

            const removedIds = slotCache.slots
              .filter((t) => t.slotBelongSceneID && deletedSceneIds.has(t.slotBelongSceneID))
              .map((t) => t.slotID);
            if (!removedIds.length) continue;

            oldSlotCaches.set(scheduleID, {
              ...slotCache,
              slots: [...slotCache.slots],
            });
            const removedSet = new Set(removedIds);
            removedSoltIdsBySchedule.set(scheduleID, removedSet);

            setSlotCache(scheduleID, {
              slots: slotCache.slots.filter((t) => !removedSet.has(t.slotID)),
            });
          }

          for (const [scheduleID, removedSet] of Array.from(removedSoltIdsBySchedule.entries())) {
            const dateCache = getCalDateCache(scheduleID);
            if (!dateCache?.calDates?.length) continue;

            oldCalDateCaches.set(scheduleID, {
              ...dateCache,
              calDates: [...dateCache.calDates],
            });

            const nextCalDates = dateCache.calDates.map((pd) => {
              const newFlat = normalizeToSortedIds(pd.calDateSlotsSort).filter((id) => !removedSet.has(id));
              const nextShift = sortIdsHasNonNoteSolt(newFlat, scheduleID, getSlotCache)
                ? pd.calDateShift
                : '';
              return { ...pd, calDateSlotsSort: newFlat, calDateShift: nextShift };
            });
            setCalDateCache(scheduleID, { calDates: nextCalDates });
          }

          const pc = getScheduleCache(breakdownId);
          const curSid = pc?.currentScheduleId;
          if (curSid && removedSoltIdsBySchedule.has(curSid)) {
            oldScheduleCurrentSlice = {
              currentSlots: pc?.currentSlots ? [...pc.currentSlots] : undefined,
              currentCalDates: pc?.currentCalDates ? [...pc.currentCalDates] : undefined,
            };
            const ptc = getSlotCache(curSid);
            const pdc = getCalDateCache(curSid);
            setScheduleCache(breakdownId, {
              currentSlots: ptc?.slots ?? [],
              currentCalDates: pdc?.calDates ?? [],
            });
          }
        }

        // 调用 API
        await deleteScene(sceneId);
      } catch (error) {
        console.error('[useSceneUpdates] 删除 Scene 失败:', error);

        // 回滚缓存
        if (breakdownId && oldCache) {
          setBreakdownCache(breakdownId, {
            scenes: oldScenes,
          });
        }
        if (breakdownId && oldElementRelations) {
          setElementRelationCache(breakdownId, { elementRelations: oldElementRelations });
        }
        for (const [scheduleID, snap] of Array.from(oldSlotCaches.entries())) {
          setSlotCache(scheduleID, snap);
        }
        for (const [scheduleID, snap] of Array.from(oldCalDateCaches.entries())) {
          setCalDateCache(scheduleID, snap);
        }
        if (breakdownId && oldScheduleCurrentSlice) {
          setScheduleCache(breakdownId, oldScheduleCurrentSlice);
        }

        throw error;
      }
    },
    [
      getBreakdownCache,
      setBreakdownCache,
      getElementRelationCache,
      setElementRelationCache,
      getScheduleCache,
      setScheduleCache,
      getSlotCache,
      setSlotCache,
      getCalDateCache,
      setCalDateCache,
    ]
  );

  /**
   * 批量删除场次行（scenes）
   */
  const batchDeleteScenesData = useCallback(
    async (sceneIds: string[], breakdownId?: string) => {
      let oldCache: any = null;
      let oldScenes: any[] = [];
      const deletedSceneIds = new Set(sceneIds);
      let oldElementRelations: ElementSceneInfo[] | null = null;
      const oldSlotCaches = new Map<string, SlotDataCache>();
      const oldCalDateCaches = new Map<string, CalDateDataCache>();
      let oldScheduleCurrentSlice: {
        currentSlots?: SlotInfo[];
        currentCalDates?: CalDateInfo[];
      } | null = null;

      try {
        // 如果提供了 breakdownId，进行乐观更新
        if (breakdownId) {
          const cache = getBreakdownCache(breakdownId);
          if (cache) {
            oldCache = { ...cache };
            oldScenes = [...cache.scenes];

            // 乐观删除缓存中的场次行
            const updatedScenes = cache.scenes.filter((scene) => !deletedSceneIds.has(scene.sceneID));

            setBreakdownCache(breakdownId, {
              scenes: updatedScenes,
            });
          }

          const rel = getElementRelationCache(breakdownId);
          if (rel?.elementRelations?.length) {
            const nextRelations = rel.elementRelations.filter((r) => !deletedSceneIds.has(r.sceneID));
            if (nextRelations.length !== rel.elementRelations.length) {
              oldElementRelations = [...rel.elementRelations];
              setElementRelationCache(breakdownId, { elementRelations: nextRelations });
            }
          }

          const removedSoltIdsBySchedule = new Map<string, Set<string>>();
          const scheduleListSlice = getScheduleCache(breakdownId);
          for (const scheduleRow of scheduleListSlice?.schedules ?? []) {
            const scheduleID = scheduleRow.scheduleID;
            const slotCache = getSlotCache(scheduleID);
            if (!slotCache?.slots?.length) continue;

            const removedIds = slotCache.slots
              .filter((t) => t.slotBelongSceneID && deletedSceneIds.has(t.slotBelongSceneID))
              .map((t) => t.slotID);
            if (!removedIds.length) continue;

            oldSlotCaches.set(scheduleID, {
              ...slotCache,
              slots: [...slotCache.slots],
            });
            const removedSet = new Set(removedIds);
            removedSoltIdsBySchedule.set(scheduleID, removedSet);

            setSlotCache(scheduleID, {
              slots: slotCache.slots.filter((t) => !removedSet.has(t.slotID)),
            });
          }

          for (const [scheduleID, removedSet] of Array.from(removedSoltIdsBySchedule.entries())) {
            const dateCache = getCalDateCache(scheduleID);
            if (!dateCache?.calDates?.length) continue;

            oldCalDateCaches.set(scheduleID, {
              ...dateCache,
              calDates: [...dateCache.calDates],
            });

            const nextCalDates = dateCache.calDates.map((pd) => {
              const newFlat = normalizeToSortedIds(pd.calDateSlotsSort).filter((id) => !removedSet.has(id));
              const nextShift = sortIdsHasNonNoteSolt(newFlat, scheduleID, getSlotCache)
                ? pd.calDateShift
                : '';
              return { ...pd, calDateSlotsSort: newFlat, calDateShift: nextShift };
            });
            setCalDateCache(scheduleID, { calDates: nextCalDates });
          }

          const pc = getScheduleCache(breakdownId);
          const curSid = pc?.currentScheduleId;
          if (curSid && removedSoltIdsBySchedule.has(curSid)) {
            oldScheduleCurrentSlice = {
              currentSlots: pc?.currentSlots ? [...pc.currentSlots] : undefined,
              currentCalDates: pc?.currentCalDates ? [...pc.currentCalDates] : undefined,
            };
            const ptc = getSlotCache(curSid);
            const pdc = getCalDateCache(curSid);
            setScheduleCache(breakdownId, {
              currentSlots: ptc?.slots ?? [],
              currentCalDates: pdc?.calDates ?? [],
            });
          }
        }

        // 批量调用删除 API
        await Promise.all(sceneIds.map((sceneId) => deleteScene(sceneId)));
      } catch (error) {
        console.error('[useSceneUpdates] 批量删除场次行（scenes）失败:', error);

        // 回滚缓存
        if (breakdownId && oldCache) {
          setBreakdownCache(breakdownId, {
            scenes: oldScenes,
          });
        }
        if (breakdownId && oldElementRelations) {
          setElementRelationCache(breakdownId, { elementRelations: oldElementRelations });
        }
        for (const [scheduleID, snap] of Array.from(oldSlotCaches.entries())) {
          setSlotCache(scheduleID, snap);
        }
        for (const [scheduleID, snap] of Array.from(oldCalDateCaches.entries())) {
          setCalDateCache(scheduleID, snap);
        }
        if (breakdownId && oldScheduleCurrentSlice) {
          setScheduleCache(breakdownId, oldScheduleCurrentSlice);
        }

        throw error;
      }
    },
    [
      getBreakdownCache,
      setBreakdownCache,
      getElementRelationCache,
      setElementRelationCache,
      getScheduleCache,
      setScheduleCache,
      getSlotCache,
      setSlotCache,
      getCalDateCache,
      setCalDateCache,
    ]
  );

  /**
   * 创建单个 Scene
   * @returns 新创建的 sceneId
   */
  const createSceneData = useCallback(
    async (breakdownId: string, data: Partial<SceneInfo>): Promise<string> => {
      try {
        // 调用 API 创建
        const response = await createScene(data);
        const newSceneId = response.data?.sceneID;

        if (!newSceneId) {
          throw new Error('创建 Scene 失败：未返回 sceneID');
        }

        // 更新缓存（添加新创建的 scene）
        const cache = getBreakdownCache(breakdownId);
        if (cache) {
          const newScene = {
            ...data,
            sceneID: newSceneId,
          } as any;

          setBreakdownCache(breakdownId, {
            scenes: [...cache.scenes, newScene],
          });
        }

        return newSceneId;
      } catch (error) {
        console.error('[useSceneUpdates] 创建 Scene 失败:', error);
        throw error;
      }
    },
    [getBreakdownCache, setBreakdownCache]
  );

  return {
    updateSceneData,
    updateScenesData,
    deleteSceneData,
    batchDeleteScenesData,
    createSceneData,
  };
}
